import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Pause, Play, Smile, Upload, X } from 'lucide-react'
import { EmojiPicker } from '@/components/chat/EmojiPicker'
import { Modal } from '@/components/settings/Modal'
import { describeError } from '@/lib/auth'
import { cx } from '@/lib/cx'
import { playUrl } from '@/lib/sounds'
import { SOUND_MAX_SECONDS, blobToDataUri, decodeSoundFile, encodeWav, nameFromFile, soundUrl, soundboardApi, waveform, type DecodedSound } from '@/lib/soundboard'
import { useVoiceStore } from '@/store/voice'
import type { Guild, ReactionEmoji, SoundboardSound } from '@/types/api'
import { Field } from '@/pages/AuthPage'
import auth from '@/pages/AuthPage.module.css'
import { SoundEmoji } from './SoundEmoji'
import styles from './SoundForm.module.css'
import { Select } from '../Select'

interface Props {
  guild: Guild
  /** Правка звука; без него — загрузка нового. */
  sound?: SoundboardSound
  /** Файл уже выбран (голосовое из чата): окно сразу открывается с волной и обрезкой. */
  file?: File
  /** На какой из этих серверов добавить — если выбор есть; guild — выбранный сначала. */
  guilds?: Guild[]
  onClose: () => void
}

const BARS = 96
/** Короче этого кусок не выбрать: полсекунды тишины звуком не назовёшь. */
const MIN_CLIP = 0.3
/** Файл больше этого не разбираем: браузер будет долго думать, а обрезать всё равно до пяти секунд. */
const MAX_FILE = 20 * 1024 * 1024
// окно эмодзи — как .compact в MediaPicker.module.css
const PICKER_W = 352
const PICKER_H = 420

const seconds = (s: number) => `${s.toFixed(1).replace('.', ',')} с`
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

/**
 * Звук сервера: загрузка нового или правка своего. Длинный файл обрезается здесь же — окно на волне
 * тянется за края и двигается целиком, кусок уходит на сервер в WAV. Файл, который подходит как есть
 * (MP3, OGG или WAV до 512 КБ и 5,2 секунды), уходит без переделки.
 */
export function SoundForm({ guild: initialGuild, sound, file: initialFile, guilds, onClose }: Props) {
  const qc = useQueryClient()
  const [guildId, setGuildId] = useState(initialGuild.id)
  const guild = guilds?.find((g) => g.id === guildId) ?? initialGuild
  const output = useVoiceStore((s) => s.prefs.audiooutput)
  const [file, setFile] = useState<DecodedSound | null>(null)
  const [range, setRange] = useState<[number, number]>([0, 0])
  const [name, setName] = useState(sound?.name ?? '')
  const [emoji, setEmoji] = useState<{ id: string | null; name: string | null }>({ id: sound?.emoji_id ?? null, name: sound?.emoji_name ?? null })
  const [volume, setVolume] = useState(sound?.volume ?? 1)
  const [busy, setBusy] = useState(false)
  const [reading, setReading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [playing, setPlaying] = useState(false)
  const [picker, setPicker] = useState<{ top: number; left: number; height: number } | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const wave = useRef<HTMLDivElement>(null)
  const emojiButton = useRef<HTMLButtonElement>(null)
  const stopPreview = useRef<(() => void) | null>(null)

  useEffect(() => () => stopPreview.current?.(), [])
  // голосовое из чата: разбираем сразу, как будто его выбрали кнопкой
  useEffect(() => {
    if (initialFile) void choose(initialFile)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const peaks = useMemo(() => (file ? waveform(file.buffer, BARS) : []), [file])
  const duration = file?.duration ?? 0

  const stop = () => {
    stopPreview.current?.()
    stopPreview.current = null
    setPlaying(false)
  }

  const choose = async (f: File | undefined) => {
    if (!f) return
    stop()
    setError(null)
    if (f.size > MAX_FILE) {
      setError('Файл больше 20 МБ. Выберите покороче')
      return
    }
    setReading(true)
    try {
      const decoded = await decodeSoundFile(f)
      setFile(decoded)
      setRange([0, Math.min(decoded.duration, SOUND_MAX_SECONDS)])
      if (!name.trim()) setName(nameFromFile(f))
    } catch {
      setError('Не получилось прочитать звук. Нужен MP3, OGG, WAV или M4A')
    } finally {
      setReading(false)
    }
  }

  // прослушать выбранный кусок (или сохранённый звук при правке) с выбранной громкостью
  const preview = async () => {
    if (playing) {
      stop()
      return
    }
    if (file) {
      const ctx = new AudioContext()
      if (output && 'setSinkId' in ctx) await (ctx as AudioContext & { setSinkId: (id: string) => Promise<void> }).setSinkId(output).catch(() => undefined)
      const src = ctx.createBufferSource()
      src.buffer = file.buffer
      const gain = ctx.createGain()
      gain.gain.value = volume
      src.connect(gain).connect(ctx.destination)
      src.onended = () => {
        void ctx.close()
        setPlaying(false)
        stopPreview.current = null
      }
      src.start(0, range[0], range[1] - range[0])
      stopPreview.current = () => {
        try {
          src.stop()
        } catch {
          /* уже доиграл */
        }
      }
      setPlaying(true)
      return
    }
    if (sound) {
      const el = await playUrl(soundUrl(sound.sound_id), { volume, sinkId: output || undefined })
      if (!el) return
      el.onended = () => {
        setPlaying(false)
        stopPreview.current = null
      }
      stopPreview.current = () => el.pause()
      setPlaying(true)
    }
  }

  // окно на волне: тянуть края или двигать целиком; нажатие мимо окна переносит его туда
  const drag = (e: ReactPointerEvent, mode: 'move' | 'start' | 'end') => {
    if (!file || !wave.current) return
    e.preventDefault()
    e.stopPropagation()
    stop()
    const width = wave.current.getBoundingClientRect().width
    const from = e.clientX
    const [a0, b0] = range
    const onMove = (ev: PointerEvent) => {
      const dt = ((ev.clientX - from) / width) * duration
      if (mode === 'move') {
        const a = clamp(a0 + dt, 0, duration - (b0 - a0))
        setRange([a, a + (b0 - a0)])
      } else if (mode === 'start') {
        setRange([clamp(a0 + dt, Math.max(0, b0 - SOUND_MAX_SECONDS), b0 - MIN_CLIP), b0])
      } else {
        setRange([a0, clamp(b0 + dt, a0 + MIN_CLIP, Math.min(duration, a0 + SOUND_MAX_SECONDS))])
      }
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const jumpTo = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!file || !wave.current) return
    const rect = wave.current.getBoundingClientRect()
    const len = range[1] - range[0]
    const at = ((e.clientX - rect.left) / rect.width) * duration
    const a = clamp(at - len / 2, 0, duration - len)
    setRange([a, a + len])
    stop()
  }

  // с клавиатуры окно двигается стрелками: на 0,1 с, с Shift — на полсекунды
  const onWindowKey = (e: ReactKeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    const step = (e.shiftKey ? 0.5 : 0.1) * (e.key === 'ArrowLeft' ? -1 : 1)
    const len = range[1] - range[0]
    const a = clamp(range[0] + step, 0, duration - len)
    setRange([a, a + len])
  }

  const openPicker = () => {
    const r = emojiButton.current?.getBoundingClientRect()
    if (!r) return
    const w = Math.min(PICKER_W, window.innerWidth - 32)
    const below = window.innerHeight - r.bottom - 16
    const above = r.top - 16
    const down = below >= PICKER_H || below >= above
    const height = Math.max(200, Math.min(PICKER_H, down ? below : above))
    setPicker({ top: down ? r.bottom + 8 : r.top - 8 - height, left: Math.max(16, Math.min(r.left, window.innerWidth - w - 16)), height })
  }

  const pickEmoji = (e: ReactionEmoji) => {
    setEmoji(e.id ? { id: e.id, name: null } : { id: null, name: e.name })
    setPicker(null)
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!sound && !file) return
    stop()
    setBusy(true)
    setError(null)
    setNameError(null)
    try {
      const body = { name: name.trim(), volume, emoji_id: emoji.id, emoji_name: emoji.id ? null : emoji.name }
      if (sound) {
        await soundboardApi.update(guild.id, sound.sound_id, body)
      } else if (file) {
        const whole = range[0] <= 0.01 && range[1] >= file.duration - 0.01
        const blob = file.asIs && whole ? file.file : encodeWav(file.buffer, range[0], range[1])
        await soundboardApi.create(guild.id, { ...body, sound: await blobToDataUri(blob) })
      }
      void qc.invalidateQueries({ queryKey: ['sounds', guild.id] })
      onClose()
    } catch (err) {
      const d = describeError(err)
      setNameError(d.fields.name ?? null)
      setError(d.fields.sound ?? d.fields.emoji_name ?? d.fields.emoji_id ?? d.fields.volume ?? (d.fields.name ? null : d.message))
      setBusy(false)
    }
  }

  const clip = range[1] - range[0]
  const nameOk = name.trim().length >= 2
  const ready = sound ? nameOk : Boolean(file) && nameOk
  return (
    <Modal title={sound ? 'Изменить звук' : 'Добавить звук'} onClose={onClose} width={480}>
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        {guilds && guilds.length > 1 && !sound ? (
          <Field label="Сервер" htmlFor="sound-guild">
            <Select id="sound-guild" value={guildId} options={guilds.map((g) => ({ value: g.id, label: g.name }))} onChange={setGuildId} />
          </Field>
        ) : null}
        {!sound ? (
          <>
            <input ref={fileInput} type="file" accept="audio/*,.mp3,.ogg,.wav,.m4a" hidden onChange={(e) => { void choose(e.target.files?.[0]); e.target.value = '' }} />
            {file ? (
              <div className={styles.trim}>
                <div className={styles.fileRow}>
                  <span className={styles.fileName} data-tooltip={file.file.name}>{file.file.name}</span>
                  <button type="button" className={styles.link} onClick={() => fileInput.current?.click()}>Другой файл</button>
                </div>
                <div ref={wave} className={styles.wave} onPointerDown={(e) => { jumpTo(e); drag(e, 'move') }}>
                  {peaks.map((p, i) => {
                    const t = ((i + 0.5) / BARS) * duration
                    return <span key={i} className={styles.bar} data-in={t >= range[0] && t <= range[1] ? '' : undefined} style={{ height: `${Math.max(6, p * 100)}%` }} />
                  })}
                  <div
                    className={styles.window}
                    style={{ left: `${(range[0] / duration) * 100}%`, width: `${(clip / duration) * 100}%` }}
                    role="slider"
                    tabIndex={0}
                    aria-label="Кусок звука"
                    aria-valuemin={0}
                    aria-valuemax={Math.round(duration * 10) / 10}
                    aria-valuenow={Math.round(range[0] * 10) / 10}
                    aria-valuetext={`с ${seconds(range[0])} по ${seconds(range[1])}`}
                    onKeyDown={onWindowKey}
                    onPointerDown={(e) => drag(e, 'move')}
                  >
                    <span className={styles.handle} data-side="start" onPointerDown={(e) => drag(e, 'start')} />
                    <span className={styles.handle} data-side="end" onPointerDown={(e) => drag(e, 'end')} />
                  </div>
                </div>
                <div className={styles.times}>
                  <button type="button" className={styles.play} aria-label={playing ? 'Остановить' : 'Прослушать кусок'} data-tooltip={playing ? 'Остановить' : 'Прослушать кусок'} onClick={() => void preview()}>
                    {playing ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <span>{seconds(range[0])} – {seconds(range[1])}</span>
                  <span className={styles.clip}>{seconds(clip)} из {seconds(Math.min(duration, SOUND_MAX_SECONDS))}</span>
                </div>
                {duration > SOUND_MAX_SECONDS ? <p className={styles.hint}>Звук длиннее 5,2 секунды. Тяните края окна или двигайте его.</p> : null}
              </div>
            ) : (
              <button type="button" className={styles.drop} disabled={reading} onClick={() => fileInput.current?.click()}>
                <Upload size={20} aria-hidden="true" />
                <span>{reading ? 'Читаем файл…' : 'Выбрать файл'}</span>
                <span className={styles.dropHint}>MP3, OGG, WAV или M4A. Длинный файл можно обрезать до 5,2 секунды</span>
              </button>
            )}
          </>
        ) : (
          <div className={styles.times}>
            <button type="button" className={styles.play} aria-label={playing ? 'Остановить' : 'Прослушать'} data-tooltip={playing ? 'Остановить' : 'Прослушать'} onClick={() => void preview()}>
              {playing ? <Pause size={16} /> : <Play size={16} />}
            </button>
            <span>Прослушать звук</span>
          </div>
        )}

        <Field label="Название" htmlFor="sound-name" error={nameError ?? undefined}>
          <input id="sound-name" className={auth.input} value={name} maxLength={32} onChange={(e) => setName(e.target.value)} placeholder="От 2 до 32 символов" />
        </Field>

        <div className={styles.row}>
          <span className={styles.label}>Эмодзи</span>
          <div className={styles.emojiRow}>
            <button
              ref={emojiButton}
              type="button"
              className={cx(styles.emojiButton, picker && styles.emojiOn)}
              aria-label="Выбрать эмодзи"
              aria-haspopup="dialog"
              aria-expanded={Boolean(picker)}
              // окно закрывается по нажатию мимо себя; без этого нажатие на кнопку закрыло бы его и тут же открыло
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => (picker ? setPicker(null) : openPicker())}
            >
              {emoji.id || emoji.name ? <SoundEmoji id={emoji.id} name={emoji.name} size={22} /> : <Smile size={20} />}
            </button>
            {emoji.id || emoji.name ? (
              <button type="button" className={styles.link} onClick={() => setEmoji({ id: null, name: null })}>
                <X size={14} aria-hidden="true" /> Убрать
              </button>
            ) : (
              <span className={styles.hint}>Виден на плитке звука и у того, кто его сыграл</span>
            )}
          </div>
        </div>

        <label className={styles.row}>
          <span className={styles.label}>Громкость</span>
          <span className={styles.volume}>
            <input type="range" min={0} max={100} step={1} value={Math.round(volume * 100)} onChange={(e) => setVolume(Number(e.target.value) / 100)} />
            <span className={styles.volumeValue}>{Math.round(volume * 100)} %</span>
          </span>
        </label>

        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <div className={styles.buttons}>
          <button type="button" className={styles.cancel} onClick={onClose}>Отмена</button>
          <button type="submit" className={styles.primary} disabled={busy || !ready}>{busy ? 'Сохраняем…' : sound ? 'Сохранить' : 'Загрузить'}</button>
        </div>
      </form>
      {picker
        ? createPortal(
            <div className={styles.picker} style={{ top: picker.top, left: picker.left, '--picker-h': `${picker.height}px` } as CSSProperties}>
              <EmojiPicker custom={guild.emojis.length ? [{ guild: guild.name, emojis: guild.emojis }] : []} onPick={pickEmoji} onClose={() => setPicker(null)} />
            </div>,
            document.body,
          )
        : null}
    </Modal>
  )
}
